import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmt } from "../../../../services/erpApi";
import { useToast } from "../../../../components/admin/ToastContext";
import { LoadingScreen } from "../../../../components/admin/EssUi";
import SalesOrderView from "./components/SalesOrderView";
import { generateInvoicePdf, downloadPdfBlob } from "../../../../utils/crmReportPdf";

// Map Lewoo API row -> SalesOrderView order prop
const mapOrderToView = (row, lines, invoicesCount) => {
  const status = row.state === "sale" ? "sales_order" : row.state === "sent" ? "quotation_sent" : "quotation";
  const orderDate = row.date_order ? fmt.date(row.date_order) + (row.date_order.includes("T") ? ` ${new Date(row.date_order).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : "") : "—";
  return {
    breadcrumb: ["Quotations", row.name || `SO${row.id}`],
    reference: row.name || `SO${row.id}`,
    status,
    customer: row.partner_name || `Partner #${row.partner_id || "—"}`,
    periodicity: row.periodicity || "Month",
    subscriptionDuration: String(row.subscription_duration ?? "1"),
    invoiceAddress: row.partner_name || row.invoice_address || row.partner_name || "—",
    deliveryAddress: row.partner_name || row.delivery_address || row.partner_name || "—",
    activateDate: row.date_order ? fmt.date(row.date_order) : "",
    bundlePackage: row.bundle_package || row.warehouse_name || "",
    orderDate,
    pricelist: row.pricelist_name || "Default XAF pricelist (CFA)",
    paymentTerms: row.payment_terms || "",
    refPurchaseOrder: row.partner_ref || "",
    billingPeriod: row.billing_period || "",
    codeFournisseur: row.code_fournisseur || "",
    isSubscription: !!row.is_subscription,
    expensesCount: row.expenses_count ?? 0,
    invoicesCount: invoicesCount ?? row.invoice_count ?? 0,
    termsUrl: "https://africasystems.com/terms",
    raw: row,
    lines: (lines || []).map((l) => ({
      product: l.product_name || l.name || "—",
      description: l.name || l.product_name || "—",
      qty: Number(l.product_uom_qty ?? l.qty ?? 0),
      delivered: Number(l.qty_delivered ?? 0),
      invoiced: Number(l.qty_invoiced ?? l.qty ?? 0),
      uom: l.uom_name || l.product_uom_name || "Units",
      price: Number(l.price_unit ?? l.price ?? 0),
      tax: l.tax_name || l.tax || "TVA 19.25% (vente)",
      subtotal: Number(l.price_subtotal ?? (Number(l.price_unit || 0) * Number(l.product_uom_qty || l.qty || 0))),
    })),
    history: row.history || [
      {
        date: row.create_date ? fmt.date(row.create_date) : new Date().toLocaleDateString("en-GB", { month: "long", day: "numeric", year: "numeric" }),
        entries: [
          {
            author: row.user_name || "System",
            time: "now",
            changes: [{ from: "Draft", to: row.state === "sale" ? "Sales Order" : "Quotation", field: "(Status)" }],
            note: row.state === "sale" ? "Quotation confirmed" : "Sales Order created",
          },
        ],
      },
    ],
  };
};

const newSalesForm = (order, lines) => ({
  partner_id: order?.partner_id || order?.raw?.partner_id || "",
  periodicity: order?.periodicity || "Month",
  subscriptionDuration: order?.subscriptionDuration || "1",
  invoiceAddress: order?.invoiceAddress || order?.raw?.partner_name || "",
  deliveryAddress: order?.deliveryAddress || order?.raw?.partner_name || "",
  activateDate: order?.raw?.date_order ? String(order.raw.date_order).slice(0,10) : order?.activateDate && order.activateDate.includes("-") ? String(order.activateDate).slice(0,10) : "",
  bundlePackage: order?.bundlePackage || "",
  orderDate: order?.raw?.date_order ? String(order.raw.date_order).slice(0,10) : new Date().toISOString().slice(0,10),
  pricelist: order?.pricelist || "Default XAF pricelist (CFA)",
  paymentTerms: order?.paymentTerms || "",
  refPurchaseOrder: order?.refPurchaseOrder || "",
  billingPeriod: order?.billingPeriod || "",
  codeFournisseur: order?.codeFournisseur || "",
  isSubscription: !!order?.isSubscription,
  lines: (lines || []).map((l) => ({
    product_id: l.product_id || "",
    product: l.product_name || l.product || "",
    description: l.name || l.description || "",
    qty: Number(l.product_uom_qty ?? l.qty ?? 1),
    delivered: Number(l.qty_delivered ?? l.delivered ?? 0),
    invoiced: Number(l.qty_invoiced ?? l.invoiced ?? 0),
    uom: l.uom_name || l.uom || "Units",
    price: Number(l.price_unit ?? l.price ?? 0),
    tax: l.tax_name || l.tax || "TVA 19.25% (vente)",
  })),
});

const SalesOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isNew = id === "new";
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [invoicesCount, setInvoicesCount] = useState(0);
  const [invoiceIds, setInvoiceIds] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const [detail, lineRows, invoices] = await Promise.all([
        api.sales.get(id).catch(() => null),
        api.sales.lines(id).catch(() => []),
        api.sales.invoices({ limit: 500 }).catch(() => []),
      ]);
      if (!detail) throw new Error("Order not found");
      setOrder(detail);
      setLines(Array.isArray(lineRows) ? lineRows : []);
      const matched = Array.isArray(invoices) ? invoices.filter((inv) => String(inv.sale_order_id) === String(id) || String(inv.origin) === String(detail.name)) : [];
      setInvoicesCount(matched.length);
      setInvoiceIds(matched.map((m) => m.lewoo_uid || m.id).filter(Boolean));
    } catch (e) {
      toast(e.message || "Load failed", "error");
    }
    setLoading(false);
  }, [id, toast, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    (async () => {
      const fetchPartners = async () => {
        let rows = await api.partners.list({ customer_rank: 1, limit: 1000 }).catch(() => []);
        let arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
        if (arr.length) return arr;
        rows = await api.partners.list({ limit: 1000 }).catch(() => []);
        arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
        return arr.filter((p) => p.is_active !== false);
      };
      const [partnerRows, productRows] = await Promise.all([
        fetchPartners().catch(() => []),
        api.products.list({ limit: 500 }).catch(() => []),
      ]);
      if (!active) return;
      setPartners(Array.isArray(partnerRows) ? partnerRows : []);
      setProducts(Array.isArray(productRows) ? productRows : []);
    })();
    return () => { active = false; };
  }, []);

  const hydrateForm = useCallback(() => {
    if (!order) return;
    const view = mapOrderToView(order, lines, invoicesCount);
    setForm(newSalesForm(view, lines));
  }, [order, lines, invoicesCount]);

  useEffect(() => { if (order && !editing) hydrateForm(); }, [order, lines]);

  const handleEdit = () => { hydrateForm(); setEditing(true); };
  const handleCancelEdit = () => { setEditing(false); hydrateForm(); };
  const handleSave = async () => {
    if (!form) return;
    try {
      // Seuls les champs backend (ROUTES.md:757 PUT /sales-orders) sont envoyés — le reste reste local-only
      const body = {};
      if (form.partner_id) body.partner_id = parseInt(form.partner_id);
      if (form.orderDate) body.date_order = `${String(form.orderDate).slice(0,10)}T00:00:00Z`;
      // state non envoyé en édition (confirm/cancel dédiés)
      if (order.state === "draft" && form.lines?.length) {
        const validLines = form.lines.filter((l) => l.product_id);
        if (validLines.length) {
          body.lines = validLines.map((l) => ({
            product_id: parseInt(l.product_id),
            name: l.description || l.product,
            product_uom_qty: Number(l.qty) || 1,
            product_uom: 1,
            price_unit: Number(l.price) || 0,
          }));
        }
      }
      // PUT strict — champs inconnus (periodicity, billing_period...) causaient Invalid value et rollback silencieux
      if (Object.keys(body).length) {
        await api.sales.update(id, body);
      }
      // Optimistic local pour les champs custom (affichage) + miroir des champs persistés
      const newView = { ...order, partner_id: body.partner_id ?? order.partner_id, partner_name: body.partner_id ? (partners.find((p) => String(p.id) === String(body.partner_id))?.display_name || order.partner_name) : order.partner_name, date_order: body.date_order || order.date_order, periodicity: form.periodicity, subscription_duration: form.subscriptionDuration, pricelist_name: form.pricelist, payment_terms: form.paymentTerms, partner_ref: form.refPurchaseOrder, billing_period: form.billingPeriod, code_fournisseur: form.codeFournisseur, bundle_package: form.bundlePackage, is_subscription: !!form.isSubscription, invoice_address: form.invoiceAddress, delivery_address: form.deliveryAddress };
      setOrder(newView);
      if (body.lines) {
        const newLines = form.lines.filter((l) => l.product_id).map((l) => ({
          product_id: l.product_id,
          name: l.description || l.product,
          product_name: products.find((p) => String(p.id) === String(l.product_id))?.name || l.product,
          product_uom_qty: Number(l.qty) || 1,
          qty: Number(l.qty) || 1,
          qty_delivered: Number(l.delivered) || 0,
          qty_invoiced: Number(l.invoiced) || 0,
          product_uom: 1,
          uom_name: l.uom || "Units",
          price_unit: Number(l.price) || 0,
          price: Number(l.price) || 0,
          price_subtotal: Number(l.qty || 0) * Number(l.price || 0),
          tax_name: l.tax || "TVA 19.25% (vente)",
        }));
        setLines(newLines);
      }
      setEditing(false);
      toast(Object.keys(body).length ? "Sale Order saved to DB" : "Sale Order updated (local only — nothing to persist)", "success");
      // Recharge depuis DB pour vérifier persistance réelle
      setTimeout(() => load(), 400);
    } catch (e) {
      console.error("[SalesOrder save] failed", e.message, e);
      toast(e.message || "Save failed — vérifie partner_id/date/lines", "error");
    }
  };

  const addLine = () => setForm((c) => ({ ...c, lines: [...(c.lines || []), { product_id: "", product: "", description: "", qty: 1, delivered: 0, invoiced: 0, uom: "Units", price: "", tax: "TVA 19.25% (vente)" }] }));
  const updateLine = (idx, patch) => setForm((c) => ({ ...c, lines: c.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const removeLine = (idx) => setForm((c) => ({ ...c, lines: c.lines.filter((_, i) => i !== idx) }));
  const chooseProduct = (idx, pid) => {
    const p = products.find((x) => String(x.id) === String(pid));
    updateLine(idx, { product_id: pid, product: p?.name || "", description: p?.name || "", price: p?.list_price || 0 });
  };

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.sales.confirm(id);
      toast("Order confirmed", "success");
      await load();
    } catch (e) { toast(e.message || "Confirm failed", "error"); }
    setBusy(false);
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel this order?")) return;
    setBusy(true);
    try {
      await api.sales.cancel(id);
      toast("Order cancelled", "success");
      await load();
    } catch (e) { toast(e.message || "Cancel failed", "error"); }
    setBusy(false);
  };

  const handleInvoice = async () => {
    setBusy(true);
    try {
      // Fix #990413/#990446: backend creates move with journal_id null -> unbalanced (débit 2 ≠ crédit 0) because lines have account_id null.
      // Force default Customer Invoices journal (id 1 sale, fallback 10) so backend can derive income account 701100
      let journalId = 1;
      try {
        const journals = await api.journals.list({ limit: 100 }).catch(() => []);
        const arr = Array.isArray(journals) ? journals : journals?.data && Array.isArray(journals.data) ? journals.data : [];
        const sale = arr.find(j => j.type === "sale" && j.is_active !== false) || arr.find(j => j.code === "INV");
        if (sale?.id) journalId = sale.id;
      } catch {}
      const inv = await api.sales.invoice(id, { journal_id: journalId });
      toast(`Invoice ${inv?.name || ""} created`, "success");
      navigate(`/admin-panel/sales/invoices/${inv?.lewoo_uid || inv?.id || ""}`);
    } catch (e) {
      const msg = e.message || "Invoice failed";
      if (msg.toLowerCase().includes("déséquilibrée") || msg.includes("debit") ) {
        toast(`${msg} — Vérifie que le produit a un compte revenu (701100) et que le journal est Customer Invoices. Produits 654/655 auto-générés n'ont pas de catégorie vente.`, "error");
      } else {
        toast(msg, "error");
      }
    }
    setBusy(false);
  };

  const handleStageChange = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      if (action === "confirm") {
        await api.sales.confirm(id);
        toast("Order confirmed → Sales Order", "success");
      } else if (action === "sent") {
        await api.sales.sendEmail(id, {}).catch(() => api.sales.confirm(id));
        toast("Quotation sent", "success");
      } else if (action === "cancel") {
        await api.sales.cancel(id);
        toast("Order set back to draft", "success");
      }
      await load();
    } catch (e) { toast(e.message || "Stage change failed", "error"); }
    setBusy(false);
  };

  const handleSubscriptionRenew = async () => {
    setBusy(true);
    try {
      await api.sales.subscriptionRenew(id, {});
      toast("Subscription renewed", "success");
      await load();
    } catch (e) {
      // fallback local optimistic if endpoint not yet deployed
      console.warn("subscriptionRenew fallback", e.message);
      toast("Subscription renewal queued (server sync pending)", "info");
    }
    setBusy(false);
  };

  const handleExpensesClick = () => {
    navigate(`/admin-panel/expenses`);
  };

  const handleDeliver = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.sales.deliver(id, {});
      toast("Order delivered — stock issued", "success");
      await load();
    } catch (e) {
      toast(e.message || "Deliver failed — vérifie le stock disponible", "error");
    }
    setBusy(false);
  };

  const buildPdfView = () => ({
    number: order.name || `SO${order.id}`,
    customer: order.partner_name || "",
    invoiceDate: order.date_order ? fmt.date(order.date_order) : "",
    dueDate: order.valid_until ? fmt.date(order.valid_until) : "",
    buyer: order.user_name || "",
    state: order.state,
    lines: lines.map((l) => ({
      label: l.name || l.product_name || "",
      product: l.product_name || "",
      qty: Number(l.product_uom_qty ?? l.qty ?? 0),
      price: Number(l.price_unit ?? 0),
      subtotal: Number(l.price_subtotal ?? 0) || Number(l.product_uom_qty ?? 0) * Number(l.price_unit ?? 0),
    })),
  });

  const handleCustomerPreview = async () => {
    if (!order) return;
    const w = window.open("", "_blank");
    if (!w) { toast("Popup bloquée — autorise les popups pour la preview", "error"); return; }
    try {
      const pdfBlob = await generateInvoicePdf({ invoice: buildPdfView() });
      const url = URL.createObjectURL(pdfBlob);
      w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast("Preview ouverte", "success");
    } catch (e) {
      try { w.location.href = `/api/sales-orders/${id}/portal`; } catch { w.close(); }
      toast(e.message || "Preview fallback portal", "info");
    }
  };

  const handlePrint = async () => {
    if (!order) { window.print(); return; }
    try {
      toast("Génération PDF...", "info");
      const pdfBlob = await generateInvoicePdf({ invoice: buildPdfView() });
      downloadPdfBlob(pdfBlob, `${order.name || `SO${order.id}`}.pdf`);
      toast("PDF téléchargé", "success");
    } catch (e) {
      console.warn("Print PDF failed, fallback window.print", e);
      window.print();
    }
  };

  const handleInvoiceClick = () => {
    if (invoicesCount === 1 && invoiceIds[0]) {
      navigate(`/admin-panel/sales/invoices/${invoiceIds[0]}`);
    } else if (invoicesCount > 1) {
      navigate(`/admin-panel/sales/invoices`);
    } else {
      handleInvoice();
    }
  };

  const handleSendEmail = async () => {
    if (!order) return;
    try {
      toast("Génération PDF devis...", "info");
      // mapper order → invoice shape pour generateInvoicePdf (même rendu)
      const view = {
        number: order.name || `SO${order.id}`,
        customer: order.partner_name || "",
        invoiceDate: order.date_order ? fmt.date(order.date_order) : "",
        dueDate: order.valid_until ? fmt.date(order.valid_until) : "",
        buyer: order.user_name || "",
        state: order.state,
        lines: lines.map((l) => ({
          label: l.name || l.product_name || "",
          product: l.product_name || "",
          qty: Number(l.product_uom_qty ?? l.qty ?? 0),
          price: Number(l.price_unit ?? 0),
          subtotal: Number(l.price_subtotal ?? 0) || Number(l.product_uom_qty ?? 0) * Number(l.price_unit ?? 0),
        })),
      };
      let filePath = null;
      try {
        const pdfBlob = await generateInvoicePdf({ invoice: view });
        const file = new File([pdfBlob], `${view.number}.pdf`, { type: "application/pdf" });
        const up = await api.upload(file);
        filePath = up?.file_path || up?.data?.file_path || null;
      } catch (e) { console.warn("PDF/upload failed, envoi sans PJ", e); }
      const payload = {};
      if (filePath) {
        payload.attachments = [{ file_path: filePath, file_name: `${view.number}.pdf`, mime_type: "application/pdf" }];
        payload.file_path = filePath;
        payload.attachment_ids = [filePath];
      }
      await api.sales.sendEmail(id, payload);
      toast(filePath ? `Email queued with PDF attached (${view.number}.pdf)` : "Email queued (PDF généré côté serveur si template le prévoit)", "success");
    } catch (e) { toast(e.message || "Send failed", "error"); }
  };

  // --- isNew : creation directe via API ---
  const [newForm, setNewForm] = useState(() => newSalesForm(null, []));
  const [newCreating, setNewCreating] = useState(false);
  const handleCreateNew = async () => {
    if (!newForm.partner_id) { toast("Customer is required", "error"); return; }
    if (!newForm.lines.some((l) => l.product_id)) { toast("Add at least one product", "error"); return; }
    setNewCreating(true);
    try {
      const body = {
        partner_id: parseInt(newForm.partner_id),
        date_order: newForm.orderDate ? `${newForm.orderDate}T00:00:00Z` : new Date().toISOString(),
        state: "draft",
        lines: newForm.lines.filter((l) => l.product_id).map((l) => ({ product_id: parseInt(l.product_id), product_uom_qty: Number(l.qty) || 1, price_unit: Number(l.price) || 0, name: l.description || l.product })),
      };
      const created = await api.sales.create(body);
      toast(`Order ${created?.name || ""} created`, "success");
      const nid = created?.id || created?.lewoo_uid;
      if (nid) navigate(`/admin-panel/sales/${nid}`);
      else navigate("/admin-panel/sales");
    } catch (e) { toast(e.message || "Create failed", "error"); }
    setNewCreating(false);
  };
  const newAddLine = (kind) => {
    if (kind === "section" || kind === "note") {
      setNewForm((c) => ({ ...c, lines: [...(c.lines || []), { product_id: "", product: kind === "section" ? "— Section —" : "Note", description: kind === "section" ? "Section" : "Note", qty: 0, delivered: 0, invoiced: 0, uom: "", price: 0, tax: "", display_type: kind === "section" ? "line_section" : "line_note" }] }));
    } else {
      setNewForm((c) => ({ ...c, lines: [...(c.lines || []), { product_id: "", product: "", description: "", qty: 1, delivered: 0, invoiced: 0, uom: "Units", price: "", tax: "TVA 19.25% (vente)" }] }));
    }
  };
  const newUpdateLine = (idx, patch) => setNewForm((c) => ({ ...c, lines: c.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const newRemoveLine = (idx) => setNewForm((c) => ({ ...c, lines: c.lines.filter((_, i) => i !== idx) }));
  const newChooseProduct = (idx, pid) => {
    const p = products.find((x) => String(x.id) === String(pid));
    newUpdateLine(idx, { product_id: pid, product: p?.name || "", description: p?.name || "", price: p?.list_price || 0 });
  };

  if (isNew) {
    const emptyOrder = {
      name: "New",
      state: "draft",
      partner_name: partners.find((p) => String(p.id) === String(newForm.partner_id))?.display_name || "",
      date_order: new Date().toISOString(),
      amount_total: 0,
      invoice_count: 0,
      lines: [],
      partner_id: newForm.partner_id,
    };
    const viewOrder = mapOrderToView(emptyOrder, newForm.lines.map((l) => ({ product_name: l.product, name: l.description, product_uom_qty: l.qty, price_unit: l.price })), 0);
    viewOrder.reference = "New";
    viewOrder.breadcrumb = ["Quotations", "New"];
    return (
      <SalesOrderView
        order={viewOrder}
        editable
        form={newForm}
        setForm={setNewForm}
        partners={partners}
        products={products}
        onBack={() => navigate("/admin-panel/sales")}
        onConfirm={handleCreateNew}
        onCancel={() => navigate("/admin-panel/sales")}
        onSave={handleCreateNew}
        onEdit={undefined}
        onCancelEdit={() => navigate("/admin-panel/sales")}
        onInvoiceClick={() => toast("Enregistre la commande avant de facturer", "info")}
        onSendEmail={() => toast("Enregistre d'abord", "info")}
        onRefresh={() => {}}
        onNew={() => navigate("/admin-panel/sales/new")}
        onAddLine={newAddLine}
        onUpdateLine={newUpdateLine}
        onRemoveLine={newRemoveLine}
        onChooseProduct={newChooseProduct}
        onPrint={handlePrint}
      />
    );
  }

  if (loading) return <LoadingScreen />;
  if (!order) return <div className="p-8 text-center text-neutral-500">Order not found</div>;

  const viewOrder = mapOrderToView(order, lines, invoicesCount);

  return (
    <SalesOrderView
      order={viewOrder}
      editable={editing}
      form={form}
      setForm={setForm}
      partners={partners}
      products={products}
      onBack={() => navigate("/admin-panel/sales")}
      onConfirm={order.state === "draft" || order.state === "sent" ? handleConfirm : undefined}
      onCancel={order.state !== "cancel" && order.state !== "done" ? handleCancel : undefined}
      onInvoiceClick={handleInvoiceClick}
      onSendEmail={handleSendEmail}
      onRefresh={load}
      onSave={handleSave}
      onEdit={handleEdit}
      onCancelEdit={handleCancelEdit}
      onAddLine={addLine}
      onUpdateLine={updateLine}
      onRemoveLine={removeLine}
      onChooseProduct={chooseProduct}
      onNew={() => navigate("/admin-panel/sales/new")}
      onStageChange={handleStageChange}
      onSubscriptionRenew={handleSubscriptionRenew}
      onExpensesClick={handleExpensesClick}
      onCustomerPreview={handleCustomerPreview}
      onPrint={handlePrint}
      onDeliver={order.state === "sale" ? handleDeliver : undefined}
    />
  );
};

export default SalesOrderDetailPage;
